/* eslint-disable react-compiler/react-compiler */
import { useListDispatchLocationsQuery } from '@/common/infrastructure/gql/generated/schema';
import { AutocompleteOption, useAutocomplete } from '@/common/presentation/hooks/useAutocomplete';

/**
 * Type representing a dispatch location from the GraphQL query result
 */
type DispatchLocation = NonNullable<
  NonNullable<NonNullable<ReturnType<typeof useListDispatchLocationsQuery>['data']>['dispatchLocations']>['items']
>[number];

export interface UseListDispatchLocationsAutocompleteParams {
  searchTerm?: string;
  debounceMs?: number;
  onDispatchLocationSelect?: (option: AutocompleteOption) => void;
  skipQuery?: boolean;
}

/**
 * Hook for implementing dispatch location autocomplete functionality
 *
 * @param params - Configuration object
 * @param params.searchTerm - The current search term to filter dispatch locations by (default: '')
 * @param params.debounceMs - Debounce time in milliseconds for search term updates (default: 300ms)
 * @param params.onDispatchLocationSelect - Optional callback when a dispatch location is selected
 * @param params.skipQuery - When true, the query is not executed (default: false)
 * @returns Object containing dispatch location options, loading state, error state, and filter function
 */
export const useListDispatchLocationsAutocomplete = ({
  searchTerm = '',
  debounceMs = 300,
  onDispatchLocationSelect: onSelect,
  skipQuery = false
}: UseListDispatchLocationsAutocompleteParams = {}) => {
  return useAutocomplete<DispatchLocation>({
    searchTerm,
    debounceMs,
    onSelect,
    query: (search?: string) => {
      const { data, loading, error } = useListDispatchLocationsQuery({
        variables: {
          skip: 0,
          where: {
            or: [
              {
                name: {
                  contains: search ?? searchTerm
                }
              }
            ]
          }
        },
        skip: skipQuery
      });
      return {
        data: { items: data?.dispatchLocations?.items },
        loading: loading,
        error: error ?? null
      };
    },
    transform: (item) => ({
      value: item.id,
      label: item.name ?? 'Unknown Location'
    })
  });
};
